import { Reminder } from '@griply/types';
import {
  EVERY_DAY_RRULE,
  parseRruleAndCountToSchedule,
  resolveTaskReminders,
} from '@griply/utils';
import { isSameDay } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  BellNotifications16,
  Calendar16,
  CalendarDeadline16,
  Check16,
  CheckCircle,
  ClockWatch16,
  DotsMenu,
  Flag,
  Heart,
  IconFormNames,
  Repeat16,
  Tag as TagIcon,
  TodoList216,
  Trash2,
} from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { FormIcon } from 'shared/components/ui/form-icon';
import { FormLinkedItemSearch } from 'shared/components/ui/form-linked-item-search';
import { FormTagsSearch } from 'shared/components/ui/form-tags-search/form-tags-search';
import { CompletionPerDayInput } from 'shared/components/ui/habit-form/completion-per-day-input';
import { CompletionPerTapInput } from 'shared/components/ui/habit-form/completion-per-tap-input';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { RemindersMenu } from 'shared/components/ui/reminders-menu';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';
import { Toggle } from 'shared/components/ui/toggle';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';
import { HabitFormFields } from 'shared/types/habit-form';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Tag } from 'shared/types/tag';
import { LinkOptions } from 'shared/types/task';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './habit-form.style';
import { RepeatFormInput } from './repeat-form-input';
import { ScheduleEndDateInput } from './schedule-end-date-input';
import { ScheduleStartDateInput } from './schedule-start-date-input';
import {
  ScheduleTimeRangeInput,
  ScheduleTimeRangeValue,
} from './schedule-time-range-input';
import { useHabitForm } from './use-habit-form';

// Prevent Enter in text/number inputs from implicitly submitting the form —
// only the focused submit button (or a focused option in a popup) should submit.
const preventImplicitSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
  const target = event.target as HTMLElement;
  if (event.key === 'Enter' && target.tagName === 'INPUT') {
    event.preventDefault();
  }
};

const defaultInitialValues: HabitFormFields = {
  name: '',
  type: TaskType.Habit,
  iconName: IconFormNames.Repeat,
  goalId: null,
};

export type HabitFormProps = {
  initialValues?: Partial<HabitFormFields>;
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  isEdit?: boolean;
  isActive?: boolean;
  isCustomIconAllowed?: boolean;
  isCompleted?: boolean;
  isArchived?: boolean;
  isRepeatTargetAllowed: boolean;
  isHabitCreationAllowed: boolean;
  onSubmit: (habit: HabitFormFields) => void;
  onCancel: () => void;
  onPremium: () => void;
  onComplete?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
};

export const HabitForm: React.FC<HabitFormProps> = ({
  initialValues,
  goals,
  lifeAreas,
  tags,
  dateFormat,
  weekStartsOn,
  timeFormat,
  isEdit,
  isActive,
  isCustomIconAllowed,
  isCompleted,
  isArchived,
  isRepeatTargetAllowed,
  isHabitCreationAllowed,
  onSubmit,
  onCancel,
  onPremium,
  onComplete,
  onArchive,
  onDelete,
}) => {
  const { t } = useTranslation();
  const today = useToday();

  // Visual-only toggles for the optional time / reminder / end-date fields.
  // State is not persisted; when a toggle is off at save time, its values are
  // stripped.
  const [showTime, setShowTime] = useState(
    () =>
      isNumber(initialValues?.startTime) || isNumber(initialValues?.duration),
  );
  const onToggleTime = () => setShowTime((prev) => !prev);

  const [showReminder, setShowReminder] = useState(
    () =>
      !!resolveTaskReminders(
        initialValues?.reminders,
        initialValues?.reminderTime,
      )?.length,
  );
  const onToggleReminder = () => setShowReminder((prev) => !prev);

  const [showEndDate, setShowEndDate] = useState(
    () => !!getActiveSchedule(initialValues?.schedules ?? [], today)?.endDate,
  );
  const onToggleEndDate = () => setShowEndDate((prev) => !prev);

  const submitWithGuards = (data: HabitFormFields) => {
    let next: HabitFormFields = { ...data };

    if (!showTime) {
      next = { ...next, startTime: null, duration: null };
    }

    if (!showReminder) {
      next = { ...next, reminders: null, reminderTime: null };
    }

    if (!showEndDate && next.schedules?.length) {
      const active = getActiveSchedule(next.schedules, today);
      if (active?.endDate) {
        next = {
          ...next,
          schedules: next.schedules.map((schedule) =>
            schedule === active ? { ...schedule, endDate: null } : schedule,
          ),
        };
      }
    }

    onSubmit(next);
  };

  const {
    control,
    onSubmit: submitForm,
    isValid,
    errors,
    watch,
    setValue,
    trigger,
    setFocus,
  } = useHabitForm(submitWithGuards, {
    ...defaultInitialValues,
    ...(initialValues ?? {}),
    iconName: initialValues?.iconName ?? defaultInitialValues.iconName,
    schedules: initialValues?.schedules ?? [
      parseRruleAndCountToSchedule(EVERY_DAY_RRULE, null, today),
    ],
  });
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const editButtonRef = useRef<HTMLDivElement>(null);

  const iconValue = watch<'iconName'>('iconName');
  const onChangeIcon = (iconName: IconFormNames) =>
    setValue<'iconName'>('iconName', iconName);

  const scheduleValue = watch<'schedules'>('schedules');
  const activeSchedule = useMemo(
    () => getActiveSchedule(scheduleValue, today),
    [scheduleValue, today],
  );
  const shouldReplaceSchedule = useMemo(
    () =>
      activeSchedule?.startDate && isSameDay(activeSchedule?.startDate, today),
    [activeSchedule?.startDate, today],
  );

  const onChangeSchedule = (schedules: HabitSchedule[] | null) => {
    setValue<'schedules'>('schedules', schedules ?? undefined);
    trigger('schedules');
  };

  const typeValue = watch<'type'>('type');
  const onChangeType = (type: TaskType.Habit | TaskType.Repeating) => {
    if (type === TaskType.Habit && !isHabitCreationAllowed) {
      onPremium();
      return;
    }

    if (type === TaskType.Repeating) {
      setValue<'hideOnTodo'>('hideOnTodo', false);
    }

    setValue<'type'>('type', type);
    trigger('type');
  };

  const hideOnTodoValue = watch<'hideOnTodo'>('hideOnTodo');
  const onChangeHideOnTodo = (hideOnTodo: boolean) => {
    setValue<'hideOnTodo'>('hideOnTodo', hideOnTodo);
    trigger('hideOnTodo');
  };

  const onToggleTaskList = () => onChangeHideOnTodo(!hideOnTodoValue);

  const reminderTime = watch<'reminderTime'>('reminderTime');
  const reminders = watch<'reminders'>('reminders');
  const onChangeReminders = (nextReminders: Reminder[]) => {
    setValue<'reminders'>('reminders', nextReminders);
    trigger('reminders');
  };

  const startTime = watch<'startTime'>('startTime');
  const duration = watch<'duration'>('duration');
  const onChangeTimeRange = (value: ScheduleTimeRangeValue) => {
    setValue<'startTime'>('startTime', value.startTime);
    setValue<'duration'>('duration', value.duration);
    trigger('startTime');
    trigger('duration');
  };

  const priorityOptions = useMemo(
    () =>
      Object.values(TaskPriorityOptions).map((priority) => ({
        label: t(`task.priority.options.${priority}.label`),
        value: priority,
      })),
    [t],
  );

  const priority = watch<'priority'>('priority');
  const onChangePriority = (priority?: string) => {
    setValue<'priority'>('priority', (priority as TaskPriorityOptions) ?? null);
    trigger('priority');
  };

  const goalId = watch<'goalId'>('goalId');
  const lifeAreaId = watch<'lifeAreaId'>('lifeAreaId');
  const onChangeLinkedItem = (link: { id: ID; type: LinkOptions } | null) => {
    const goalId = link?.type === LinkOptions.Goal ? link.id : null;
    const lifeAreaId = link?.type === LinkOptions.LifeArea ? link.id : null;
    setValue<'goalId'>('goalId', goalId);
    setValue<'lifeAreaId'>('lifeAreaId', lifeAreaId);
    trigger('goalId');
    trigger('lifeAreaId');
  };

  const tagIds = watch<'tagIds'>('tagIds');
  const onChangeTagIds = (ids: ID[]) => {
    setValue<'tagIds'>('tagIds', ids);
    trigger('tagIds');
  };

  const completeHabit = () => {
    onComplete?.();
    closeMenu();
  };

  const archiveHabit = () => {
    onArchive?.();
    closeMenu();
  };

  const deleteHabit = () => {
    onDelete?.();
    closeMenu();
  };

  useEffect(() => {
    if (!isEdit && isActive) {
      setFocus('name');
    }
  }, [isActive, isEdit, setFocus]);

  return (
    <Styled.Form
      noValidate
      onSubmit={submitForm}
      onKeyDown={preventImplicitSubmit}
    >
      <Styled.ScrollArea>
        <Styled.Header>
          <FormIcon
            value={iconValue ?? IconFormNames.Repeat}
            onChange={onChangeIcon}
            isPremium={!isCustomIconAllowed}
            onPremium={onPremium}
            large
          />

          <Styled.TitleColumn>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <ResizableTextArea
                  ref={field.ref}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  variant={ResizableTextAreaVariants.Subtitle1}
                  placeholder={t('forms.habit.fields.name.placeholder')}
                  fullWidth
                />
              )}
            />
          </Styled.TitleColumn>
        </Styled.Header>

        <Styled.Body>
          <Divider />

          <Styled.Section>
            <Styled.Item>
              <Styled.Label>
                <Icon icon={Repeat16} />
                <Styled.LabelText>
                  {t('forms.habit.fields.repeat.label')}
                </Styled.LabelText>
              </Styled.Label>
              <RepeatFormInput
                onChange={onChangeSchedule}
                value={scheduleValue ?? []}
                type={typeValue}
                onChangeType={onChangeType}
                weekStartsOn={weekStartsOn}
                targetAllowed={isRepeatTargetAllowed}
                createHabitAllowed={isHabitCreationAllowed}
                onPremium={onPremium}
                error={errors.schedules?.message}
                shouldReplaceSchedule={shouldReplaceSchedule}
                isSmall
              />
            </Styled.Item>

            {!!activeSchedule && !activeSchedule?.frequency && (
              <Styled.Item>
                <Styled.Label>
                  <Icon icon={Check16} />
                  <Styled.LabelText>
                    {t('forms.habit.fields.completions-per-day.label')}
                  </Styled.LabelText>
                </Styled.Label>
                <CompletionPerDayInput
                  schedules={scheduleValue ?? []}
                  onChange={onChangeSchedule}
                  shouldReplaceSchedule={shouldReplaceSchedule}
                />
              </Styled.Item>
            )}

            <CompletionPerTapInput
              schedules={scheduleValue ?? []}
              onChange={onChangeSchedule}
              shouldReplaceSchedule
            />
          </Styled.Section>
          <Divider />
          <Styled.Section>
            {!!activeSchedule && (
              <Styled.Item>
                <Styled.Label>
                  <Icon icon={Calendar16} />
                  <Styled.LabelText>
                    {t('forms.habit.fields.start-date.label')}
                  </Styled.LabelText>
                </Styled.Label>
                <ScheduleStartDateInput
                  today={today}
                  schedules={scheduleValue ?? []}
                  weekStartsOn={weekStartsOn}
                  dateFormat={dateFormat}
                  onChange={onChangeSchedule}
                />
              </Styled.Item>
            )}

            <Styled.Item>
              <Styled.Label>
                <Icon icon={ClockWatch16} />
                <Styled.LabelText>
                  {t('forms.habit.fields.time.label')}
                </Styled.LabelText>
              </Styled.Label>
              <ScheduleTimeRangeInput
                enabled={showTime}
                onToggleEnabled={onToggleTime}
                startTime={startTime}
                duration={duration}
                timeFormat={timeFormat}
                onChange={onChangeTimeRange}
              />
            </Styled.Item>

            {!!activeSchedule && (
              <Styled.Item>
                <Styled.Label>
                  <Icon icon={CalendarDeadline16} />
                  <Styled.LabelText>
                    {t('forms.habit.fields.end-date.label')}
                  </Styled.LabelText>
                </Styled.Label>
                <ScheduleEndDateInput
                  enabled={showEndDate}
                  onToggleEnabled={onToggleEndDate}
                  today={today}
                  schedules={scheduleValue ?? []}
                  weekStartsOn={weekStartsOn}
                  dateFormat={dateFormat}
                  onChange={onChangeSchedule}
                />
              </Styled.Item>
            )}
          </Styled.Section>
          <Divider />

          <Styled.Section>
            <Styled.Item>
              <Styled.Label>
                <Icon icon={Heart} />
                <Styled.LabelText>
                  {t('forms.habit.fields.link-to.label')}
                </Styled.LabelText>
              </Styled.Label>
              <FormLinkedItemSearch
                goals={goals}
                lifeAreas={lifeAreas}
                value={goalId ?? lifeAreaId}
                onChange={onChangeLinkedItem}
                placeholder={t('forms.habit.fields.link-to.placeholder')}
              />
            </Styled.Item>
          </Styled.Section>

          <Divider />

          <Styled.Section>
            <Styled.Item>
              <Styled.Label>
                <Icon icon={Flag} />
                <Styled.LabelText>
                  {t('forms.habit.fields.priority.label')}
                </Styled.LabelText>
              </Styled.Label>
              <CustomFormSelect
                value={priority ?? undefined}
                placeholder={t('forms.habit.fields.priority.placeholder')}
                options={priorityOptions}
                onChange={onChangePriority}
                size={SelectSize.ExtraSmall}
                canClear
              />
            </Styled.Item>

            <Styled.Item>
              <Styled.Label>
                <Icon icon={TagIcon} />
                <Styled.LabelText>
                  {t('forms.habit.fields.tags.label')}
                </Styled.LabelText>
              </Styled.Label>
              <FormTagsSearch
                tags={tags}
                value={tagIds}
                onChange={onChangeTagIds}
                placeholder={t('forms.habit.fields.tags.placeholder')}
              />
            </Styled.Item>
            <Styled.Item>
              <Styled.Label>
                <Icon icon={BellNotifications16} />
                <Styled.LabelText>
                  {t('forms.habit.fields.reminder-time.label')}
                </Styled.LabelText>
              </Styled.Label>
              <Styled.ToggleRow>
                <Toggle
                  value="reminder"
                  checked={showReminder}
                  onChange={onToggleReminder}
                />
                {showReminder && (
                  <Styled.ToggleRowContent>
                    <Styled.Label>
                      {t('forms.habit.fields.reminder-time.label')}
                    </Styled.Label>
                    <RemindersMenu
                      reminders={reminders}
                      reminderTime={reminderTime}
                      hasTime={isNumber(activeSchedule?.timeslot?.startTime)}
                      timeFormat={timeFormat}
                      onChange={onChangeReminders}
                      placeholder={t('reminders.add')}
                    />
                  </Styled.ToggleRowContent>
                )}
              </Styled.ToggleRow>
            </Styled.Item>
            <Styled.Item $autoWidthField>
              <Styled.Label $disabled={typeValue !== TaskType.Habit}>
                <Icon icon={TodoList216} />
                <Styled.LabelText>
                  {t('general.labels.habit.repeat.hide-on-todo.label')}
                </Styled.LabelText>
              </Styled.Label>
              <Styled.ToggleRow>
                <Toggle
                  value="showInTaskList"
                  checked={!hideOnTodoValue}
                  onChange={onToggleTaskList}
                  disabled={typeValue !== TaskType.Habit}
                />
              </Styled.ToggleRow>
            </Styled.Item>
          </Styled.Section>
        </Styled.Body>
      </Styled.ScrollArea>

      <Styled.Footer>
        {!!isEdit && (
          <Styled.EditButtonContainer ref={editButtonRef}>
            <Button
              variant={Variants.Outlined}
              size={Sizes.Medium}
              endIcon={DotsMenu}
              color={Colors.Inherit}
              onClick={openMenu}
              type="button"
            >
              {t('forms.habit.buttons.more-options.label')}
            </Button>
            {menuOpen && (!!onComplete || !!onArchive || !!onDelete) && (
              <PopupMenu
                referenceElement={editButtonRef}
                position="top-start"
                onOutside={closeMenu}
              >
                {!!onComplete && (
                  <PopupMenuList hasBottomBorder={!!onArchive || !!onDelete}>
                    <PopupMenuListItem>
                      <PopupMenuButton
                        onClick={completeHabit}
                        start={<Icon icon={CheckCircle} />}
                      >
                        {t(
                          isCompleted
                            ? 'forms.habit.context-menu.un-complete.label'
                            : 'forms.habit.context-menu.complete.label',
                        )}
                      </PopupMenuButton>
                    </PopupMenuListItem>
                  </PopupMenuList>
                )}
                {(!!onArchive || !!onDelete) && (
                  <PopupMenuList>
                    {!!onArchive && (
                      <PopupMenuListItem>
                        <PopupMenuButton
                          onClick={archiveHabit}
                          start={<Icon icon={Archive} />}
                        >
                          {t(
                            isArchived
                              ? 'forms.habit.context-menu.de-archive.label'
                              : 'forms.habit.context-menu.archive.label',
                          )}
                        </PopupMenuButton>
                      </PopupMenuListItem>
                    )}
                    {!!onDelete && (
                      <PopupMenuListItem>
                        <PopupMenuButton
                          onClick={deleteHabit}
                          start={<Icon icon={Trash2} />}
                        >
                          {t('forms.habit.context-menu.delete.label')}
                        </PopupMenuButton>
                      </PopupMenuListItem>
                    )}
                  </PopupMenuList>
                )}
              </PopupMenu>
            )}
          </Styled.EditButtonContainer>
        )}

        <Styled.FormButtons>
          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={Colors.White}
            onClick={onCancel}
            type="button"
          >
            {t('forms.habit.buttons.cancel.label')}
          </Button>
          <Button size={Sizes.Medium} type="submit" disabled={!isValid}>
            {t('forms.habit.buttons.save.label')}
          </Button>
        </Styled.FormButtons>
      </Styled.Footer>
    </Styled.Form>
  );
};
